/**
 * Legal pages rendered from the repo's Markdown (content/legal, see the README
 * there): the Terms of Service as immutable versions listed in
 * content/legal/terms/versions.json, and the Privacy Policy.
 *
 * Only for the repo's own legal Markdown, never for user input (output is used with v-html).
 *
 * The pages are prerendered, so the HTML travels in the page and its payload;
 * marked and the raw Markdown are dynamic imports the client only loads when
 * it renders a page itself (dev, or a missing payload).
 */
import termsManifest from '~~/content/legal/terms/versions.json'

export interface TermsVersion {
  /** The value of LEGAL_TERMS_VERSION in the API; /terms/<label> */
  label: string
  /** "Stand" date, YYYY-MM-DD */
  date: string
  /** <label>.md in content/legal/terms */
  file: string
  /** Stage tests only: never the current version while a published one exists, not indexed */
  draft?: true
}

export interface LoadedTermsVersion {
  version: TermsVersion
  html: string
}

/* nuxt.config.ts validates the manifest at build time (label pattern, file
   name, {#dpa} heading), so the pages can trust it here. */
const versions = termsManifest as TermsVersion[]

/* Keyed by file name: the glob keys are paths whose form depends on the alias. */
const termsSources: Record<string, () => Promise<string>> = Object.fromEntries(
  Object.entries(
    import.meta.glob<string>('~~/content/legal/terms/*.md', { query: '?raw', import: 'default' })
  ).map(([path, load]) => [path.slice(path.lastIndexOf('/') + 1), load])
)

const privacySource = () => import('~~/content/legal/privacy.md?raw').then(m => m.default)

/** All Terms versions, oldest first (manifest order). */
export function termsVersions(): readonly TermsVersion[] {
  return versions
}

export function findTermsVersion(label: string): TermsVersion | undefined {
  return versions.find(v => v.label === label)
}

/** The version /terms shows: the newest entry without `draft`, else the newest draft. */
export function latestPublishedTerms(): TermsVersion {
  const published = versions.filter(v => !v.draft)
  return published.at(-1) ?? versions.at(-1)!
}

/** "Stand" date in German notation (23.10.2026); no Date object, so no time zone shift. */
export function formatTermsDate(date: string): string {
  const [year, month, day] = date.split('-')
  return `${day}.${month}.${year}`
}

/** The line above the text: "Fassung <label>, Stand <date>", or the draft marker. */
export function termsStatusLine(version: TermsVersion): string {
  return version.draft
    ? 'Entwurf – nicht gültig'
    : `Fassung ${version.label}, Stand ${formatTermsDate(version.date)}`
}

export async function loadTermsVersion(label: string): Promise<LoadedTermsVersion | null> {
  const version = findTermsVersion(label)
  const load = version && termsSources[version.file]
  if (!version || !load) return null
  return { version, html: await renderLegalMarkdown(await load()) }
}

export async function loadPrivacyPolicy(): Promise<string> {
  return renderLegalMarkdown(await privacySource())
}

/* ── Rendering ─────────────────────────────────────────────────────────── */

/** `## Heading {#id}` fixes the anchor (the Terms keep `{#dpa}`: dashboard and docs link it). */
const EXPLICIT_ID = /\s*\{#([a-z0-9-]+)\}\s*$/
const ALLOWED_SCHEME = /^(?:https?|mailto|tel):/i
const ANY_SCHEME = /^[a-z][a-z0-9+.-]*:/i

function slugify(text: string): string {
  const slug = text
    .replace(EXPLICIT_ID, '')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/§/g, '')
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || 'section'
}

function escapeAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/** http(s), mailto, tel and relative links; anything else (javascript:, data:) renders as text. */
function safeHref(href: string): string | null {
  const trimmed = href.trim()
  if (ANY_SCHEME.test(trimmed) && !ALLOWED_SCHEME.test(trimmed)) return null
  try {
    return encodeURI(trimmed).replace(/%25/g, '%')
  }
  catch {
    return null
  }
}

function opensNewTab(href: string): boolean {
  if (!/^https?:\/\//i.test(href)) return false
  try {
    return new URL(href).hostname !== 'clarify.bot'
  }
  catch {
    return false
  }
}

async function renderLegalMarkdown(source: string): Promise<string> {
  const { Marked } = await import('marked')
  const marked = new Marked({ gfm: true, async: false })

  /* Explicit ids are reserved before rendering, so an automatic id earlier in
     the text can never take `dpa` away from the DPA heading. `main` is the
     page's own landmark id. */
  const reserved = new Set<string>(['main'])
  const used = new Set<string>()
  const tokens = marked.lexer(source)
  marked.walkTokens(tokens, (token) => {
    if (token.type !== 'heading') return
    const match = (token.text as string).match(EXPLICIT_ID)
    if (match) reserved.add(match[1]!)
  })

  const claim = (base: string, explicit: boolean): string => {
    let id = base
    for (let n = 2; used.has(id) || (!explicit && reserved.has(id)); n++) id = `${base}-${n}`
    used.add(id)
    return id
  }

  marked.use({
    renderer: {
      heading({ tokens: inline, depth, text }) {
        let html = this.parser.parseInline(inline)
        const match = html.match(EXPLICIT_ID)
        let id: string
        if (match) {
          html = html.slice(0, match.index)
          id = claim(match[1]!, true)
        }
        else {
          id = claim(slugify(text), false)
        }
        return `<h${depth} id="${id}">${html}</h${depth}>\n`
      },
      link({ href, title, tokens: inline }) {
        const text = this.parser.parseInline(inline)
        const safe = safeHref(href)
        if (safe === null) return text
        const titleAttr = title ? ` title="${escapeAttribute(title)}"` : ''
        const target = opensNewTab(safe) ? ' target="_blank" rel="noopener noreferrer"' : ''
        return `<a href="${escapeAttribute(safe)}"${titleAttr}${target}>${text}</a>`
      }
    }
  })

  return marked.parser(tokens) as string
}
